import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { CreditCard, Building2, Download, Search, Filter, CheckCircle, Clock, XCircle, AlertCircle, TrendingUp, Euro, FileText, Loader2, ChevronLeft, ChevronRight, Eye, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ConventionPaymentDetailModal from './ConventionPaymentDetailModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Tableau de bord des conventions payantes
 * Avec filtres par statut, méthode de paiement et export CSV
 */
export default function ConventionPaymentsDashboard() {
  const { getAuthHeader } = useAuth();
  
  // États
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [conventions, setConventions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filtres
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal détail
  const [selectedConvention, setSelectedConvention] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Export en cours
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchConventions();
  }, []);

  useEffect(() => {
    fetchConventions();
  }, [page, statusFilter, methodFilter, dateFrom, dateTo, searchQuery]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API}/api/convention-payments/dashboard/stats`,
        { headers: getAuthHeader() }
      );
      setStats(response.data);
    } catch {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const fetchConventions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15'
      });
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (methodFilter !== 'all') params.append('method', methodFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${API}/api/convention-payments/dashboard/list?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      
      setConventions(response.data.conventions);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch {
      toast.error('Erreur lors du chargement des conventions');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (methodFilter !== 'all') params.append('method', methodFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(
        `${API}/api/convention-payments/dashboard/export/csv?${params.toString()}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conventions_paiements_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export CSV téléchargé !');
    } catch {
      toast.error('Erreur lors de l\'export CSV');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setPage(1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { bg: 'bg-green-700', icon: <CheckCircle className="h-3 w-3" />, label: 'Payé' },
      pending: { bg: 'bg-yellow-700', icon: <Clock className="h-3 w-3" />, label: 'En attente' },
      pending_virement: { bg: 'bg-blue-700', icon: <Building2 className="h-3 w-3" />, label: 'Virement en attente' },
      pending_stripe: { bg: 'bg-purple-700', icon: <CreditCard className="h-3 w-3" />, label: 'Stripe en attente' },
      failed: { bg: 'bg-red-700', icon: <XCircle className="h-3 w-3" />, label: 'Échoué' },
    };
    
    const badge = badges[status] || { bg: 'bg-gray-700', icon: <AlertCircle className="h-3 w-3" />, label: 'Non démarré' };
    
    return (
      <Badge className={`${badge.bg} text-white border-transparent flex items-center gap-1 font-semibold`}>
        {badge.icon}
        <span>{badge.label}</span>
      </Badge>
    );
  };

  const getMethodBadge = (method) => {
    if (method === 'stripe') {
      return (
        <Badge className="bg-purple-700 text-white border-transparent font-semibold">
          <CreditCard className="h-3 w-3 mr-1" /> Carte
        </Badge>
      );
    }
    if (method === 'virement') {
      return (
        <Badge className="bg-green-700 text-white border-transparent font-semibold">
          <Building2 className="h-3 w-3 mr-1" /> Virement
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-600 text-white border-transparent font-semibold">-</Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr.slice(0, 10);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="convention-payments-dashboard">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Euro className="h-6 w-6" />
            Tableau de Bord - Conventions Payantes
          </CardTitle>
          <CardDescription className="text-white/90">
            Suivi des paiements et export comptable
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Payé */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Payé</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_status.paid.count}</p>
                  <p className="text-sm text-green-600">{formatMoney(stats.by_status.paid.total)}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500 opacity-30" />
              </div>
            </CardContent>
          </Card>

          {/* En attente virement */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Virement en attente</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_status.pending_virement.count}</p>
                  <p className="text-sm text-blue-600">{formatMoney(stats.by_status.pending_virement.total)}</p>
                </div>
                <Building2 className="h-10 w-10 text-blue-500 opacity-30" />
              </div>
            </CardContent>
          </Card>

          {/* En attente */}
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_status.pending.count}</p>
                  <p className="text-sm text-yellow-600">{formatMoney(stats.by_status.pending.total)}</p>
                </div>
                <Clock className="h-10 w-10 text-yellow-500 opacity-30" />
              </div>
            </CardContent>
          </Card>

          {/* Échoué */}
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Échoué</p>
                  <p className="text-2xl font-bold text-red-600">{stats.by_status.failed.count}</p>
                  <p className="text-sm text-red-600">{formatMoney(stats.by_status.failed.total)}</p>
                </div>
                <XCircle className="h-10 w-10 text-red-500 opacity-30" />
              </div>
            </CardContent>
          </Card>

          {/* Total */}
          <Card className="border-l-4 border-l-purple-500 bg-purple-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.totals.conventions}</p>
                  <p className="text-sm text-purple-600">{formatMoney(stats.totals.amount)}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres et Export */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtres
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetFilters}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Réinitialiser
              </Button>
              <Button 
                onClick={handleExportCSV}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="export-csv-button"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Recherche */}
            <div className="lg:col-span-2">
              <Label htmlFor="search">Recherche</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Titre, numéro, destinataire..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Statut */}
            <div>
              <Label>Statut paiement</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="paid">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Payé
                    </span>
                  </SelectItem>
                  <SelectItem value="pending_virement">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      Virement en attente
                    </span>
                  </SelectItem>
                  <SelectItem value="pending">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      En attente
                    </span>
                  </SelectItem>
                  <SelectItem value="failed">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      Échoué
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Méthode */}
            <div>
              <Label>Méthode</Label>
              <Select value={methodFilter} onValueChange={(v) => { setMethodFilter(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les méthodes</SelectItem>
                  <SelectItem value="stripe">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />
                      Carte bancaire
                    </span>
                  </SelectItem>
                  <SelectItem value="virement">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      Virement
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Période */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>Du</Label>
                <DatePickerFr
                  className="w-full"
                  value={dateFrom}
                  onChange={(v) => { setDateFrom(v); setPage(1); }}
                />
              </div>
              <div className="flex-1">
                <Label>Au</Label>
                <DatePickerFr
                  className="w-full"
                  value={dateTo}
                  onChange={(v) => { setDateTo(v); setPage(1); }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Conventions ({total})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : conventions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Aucune convention trouvée</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Convention</TableHead>
                      <TableHead>Destinataire</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Méthode</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conventions.map((conv) => (
                      <TableRow key={conv.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{conv.titre || 'Sans titre'}</p>
                            <p className="text-xs text-gray-500">{conv.numero_contrat}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{conv.destinataire_nom || '-'}</p>
                            <p className="text-xs text-gray-500">{conv.destinataire_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatMoney(conv.montant)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(conv.payment_status)}
                        </TableCell>
                        <TableCell>
                          {getMethodBadge(conv.payment_method)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(conv.payment_date || conv.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedConvention(conv);
                              setShowDetailModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Page {page} sur {totalPages} ({total} résultats)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Détail */}
      <ConventionPaymentDetailModal
        showDetailModal={showDetailModal}
        setShowDetailModal={setShowDetailModal}
        selectedConvention={selectedConvention}
        formatMoney={formatMoney}
        formatDate={formatDate}
        getStatusBadge={getStatusBadge}
        getMethodBadge={getMethodBadge}
      />
    </div>
  );
}
